import { useState, useEffect, useRef, useCallback } from 'react'
import connectivityStore from '@/redux/slices/connectivitySlice'

type OfflineCallback = () => void
type Unsubscribe = () => void

export function useOffline() {
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true)
  // A till that is ALREADY offline when the hook mounts never fires the
  // 'offline' event, so seeding from navigator keeps the flag truthful.
  const [wasOffline, setWasOffline] = useState(typeof navigator !== 'undefined' ? !navigator.onLine : false)
  const onOnlineCallbacks = useRef<Array<OfflineCallback>>([])
  const onOfflineCallbacks = useRef<Array<OfflineCallback>>([])

  useEffect(() => {
    // Mirror the initial-state `navigator` guard: registering listeners here
    // unconditionally would throw `ReferenceError: window is not defined` in
    // SSR / non-DOM environments.
    if (typeof window === 'undefined') return

    const handleOnline = () => {
      setIsOnline(true)
      setWasOffline(false)
      // Mirror into the shared connectivity model (useConnectivity reads it;
      // marking is change-gated so duplicate hooks are harmless).
      connectivityStore.markNavigatorOnline(true)
      // Iterate a copy: a callback may unsubscribe (or register) while running,
      // mutating the live array mid-forEach and skipping a sibling callback.
      ;[...onOnlineCallbacks.current].forEach(callback => callback())
    }

    const handleOffline = () => {
      setIsOnline(false)
      setWasOffline(true)
      connectivityStore.markNavigatorOnline(false)
      ;[...onOfflineCallbacks.current].forEach(callback => callback())
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Registering returns an unsubscribe function — callers that register from
  // an effect MUST clean up, otherwise remounts accumulate callbacks and fire
  // duplicate (and stale) syncs.
  const onOnline = useCallback((callback: OfflineCallback): Unsubscribe => {
    onOnlineCallbacks.current.push(callback)
    return () => {
      onOnlineCallbacks.current = onOnlineCallbacks.current.filter(cb => cb !== callback)
    }
  }, [])

  const onOffline = useCallback((callback: OfflineCallback): Unsubscribe => {
    onOfflineCallbacks.current.push(callback)
    return () => {
      onOfflineCallbacks.current = onOfflineCallbacks.current.filter(cb => cb !== callback)
    }
  }, [])

  return {
    isOnline,
    wasOffline,
    onOnline,
    onOffline
  }
}
